/**
 * The sample image viewer: an OpenSeadragon canvas with pan/zoom, optional mask
 * overlays (toggled with Ctrl), and native viewport rotation via the `rotation`
 * prop (e.g. -90 to show portrait scans as landscape).
 *
 * Everything on screen is a layer in the one OSD world, bottom to top:
 *
 *     thumb  → the low-res placeholder      (always present, cheap)
 *     base   → the full-res Cantaloupe tiles
 *     masks  → gallery, then roots          (opacity 0 unless Ctrl)
 *
 * Keeping the thumbnail *in* the world rather than as a DOM <img> is what makes
 * the rest simple: it pans, zooms and rotates with the viewport for free, so it
 * stays correct at any magnification and never has to be hidden.
 *
 * What the user sees, in order:
 *   1. the thumbnail, as soon as it loads (ImageList prefetches ahead, so this
 *      is usually instant);
 *   2. the full-res tiles, but only once the image has stayed selected for
 *      VIEWER_LOAD_DEBOUNCE_MS — stepping and playback never pay for tiles they
 *      are about to throw away;
 *   3. a swap that only happens once the incoming layer has fully loaded, so a
 *      frame is never blank.
 *
 * A scanner's black frame is cropped off with OSD's per-layer clip (`crop`).
 * OSD 5 computes the home bounds from the *clipped* bounds of the world's
 * items, so once every layer carries the clip, fitting, the zoomed test and
 * the zoom-out limit all follow the cropped image with nothing else to do.
 *
 * Every layer is tracked by the image id it came from, so the thumbnail, the
 * full-res image and the masks can never show three different acquisitions. A
 * full-res image that has fallen behind the selection is hidden rather than
 * shown: a sharp picture of the *previous* acquisition is worse than a blurry
 * one of the current.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    useViewerImageUrl,
    useViewerThumbnailUrl,
    useMaskUrls,
} from '@/services/image.service';
import { useParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import type { ImageModel } from '@/types/models';
import type { ViewerFocus } from '@/lib/acquisition';
import {
    type Crop,
    croppedSize,
    hasCrop,
    imageDpi,
    NO_CROP,
    pxToMm,
} from '@/lib/crop';
import type { DepthSetup } from '@/lib/depth';
import { useTranslation } from 'react-i18next';
import OpenSeadragon, { Viewer } from 'openseadragon';
import {
    ImageOffIcon,
    Loader2Icon,
    HomeIcon,
    MaximizeIcon,
    MinimizeIcon,
    ZoomInIcon,
    ZoomOutIcon,
} from 'lucide-react';
import { ViewerButton, ViewerToolbar } from './ViewerControls';
import DepthScale, { DEPTH_GUTTER_PX } from './DepthScale';

// The raw jp2 at 300 dpi. Only an estimate until the full-res layer has said
// what it really is.
const IMAGE_WIDTH = 2550;
const IMAGE_HEIGHT = 3507;
const IMAGE_DPI = 300;
const MASK_OPACITY = 0.5;
// How many raw-image pixels a `focus` deep link puts across the screen. Wide
// enough to keep the surroundings of the annotated point readable, tight enough
// that the point itself is unambiguous.
const FOCUS_SPAN_PX = 1500;
// Zooming to an animal frames its box with this much context around it, and
// never closer than this many raw-image pixels across, so a tiny mite still
// shows where it sits.
const ANIMAL_CONTEXT = 3;
const ANIMAL_MIN_SPAN_PX = 600;
// The animal's box flashes this long once the zoom has landed, the last
// ANIMAL_FADE_MS fading out.
const ANIMAL_FLASH_MS = 1000;
const ANIMAL_FADE_MS = 300;
// Starts the flash anyway when no zoom animation runs (already on the animal).
const ANIMAL_FLASH_FALLBACK_MS = 1500;
// How long an image has to stay selected before we fetch its full-res tiles.
const VIEWER_LOAD_DEBOUNCE_MS = 1000;
// Concurrent tile requests. OSD's default is 1 per image and the old value
// here was 2, which serialised a screenful of tiles behind each other; with
// Cantaloupe taking whole seconds per uncached tile that is the difference
// between "slow" and "unusable". 6 matches what a browser will open to one
// origin over HTTP/1.1, so going higher just queues in the browser instead.
const TILE_LOADER_LIMIT = 6;
// How long the previous acquisition has to stay up in place of the selected
// one before we say so. Stepping onto an image whose thumbnail is already warm
// swaps within this, and a spinner flashing on every step would be noise.
const STALE_INDICATOR_DELAY_MS = 250;
// Zoom factor of one click on the zoom buttons.
const ZOOM_STEP = 1.6;

/* -------------------------------------------------------------------------
 * Diagnostics
 *
 * Off by default, and switchable at runtime so a slow viewer can be diagnosed
 * on the deployed app without shipping a build:
 *
 *     localStorage.setItem('osd:debug', '1'); location.reload();
 *     localStorage.removeItem('osd:debug');
 *
 * What comes out, per full-res layer: when the swap started, every tile that
 * failed, and a summary line when the layer finishes with the wall-clock time
 * and the tile count. Read it next to the Cantaloupe access log — that log
 * says how long the *server* took per tile, this one says how long the user
 * actually waited and how many tiles it took to get there.
 * ---------------------------------------------------------------------- */
const DEBUG =
    typeof window !== 'undefined' &&
    window.localStorage?.getItem('osd:debug') === '1';

function log(event: string, detail?: Record<string, unknown>) {
    if (!DEBUG) return;
    console.debug(
        `[osd] ${event}`,
        detail ? { t: Math.round(performance.now()), ...detail } : '',
    );
}

/** Short, greppable name for an image: the trailing acquisition file name. */
const shortId = (url: string) =>
    decodeURIComponent(url).split('/').slice(-2, -1)[0] ?? url;

// The world, bottom to top. Draw order is array order, so index 0 is drawn
// first and everything after it lands on top.
const STACK = ['thumb', 'base', 'gallery', 'roots'] as const;
type Slot = (typeof STACK)[number];
type Layers = Record<Slot, OpenSeadragon.TiledImage | null>;

const noLayers = (): Layers => ({
    thumb: null,
    base: null,
    gallery: null,
    roots: null,
});

// An installed layer: which image it shows, and the selection it was requested
// for. Comparing ticks tells us which of two lagging layers is less stale.
type LayerState = { id: string; tick: number } | null;

type Size = { x: number; y: number };

/**
 * The clip that takes `crop` off `item`. Both are in millimetres against the
 * scan's physical size, so the clip is the same fraction of every layer —
 * full-res, thumbnail or mask, at whatever DPI it was acquired — and applying
 * it needs nothing from the layer but its own pixel size. Null clears the clip.
 */
function clipFor(
    item: OpenSeadragon.TiledImage,
    crop: Crop | null | undefined,
    physical: Size,
): OpenSeadragon.Rect | null {
    if (!hasCrop(crop)) return null;
    const px = item.getContentSize();
    const kx = px.x / physical.x;
    const ky = px.y / physical.y;
    const kept = croppedSize(physical.x, physical.y, crop);
    return new OpenSeadragon.Rect(
        crop.left * kx,
        crop.top * ky,
        kept.width * kx,
        kept.height * ky,
    );
}

/** Put the known layers back in STACK order. Anything else in the world is a
 *  swap still loading; those are fully transparent, so where they land while
 *  they wait doesn't matter. */
function restack(viewer: Viewer, layers: Layers) {
    let index = 0;
    for (const slot of STACK) {
        const item = layers[slot];
        if (!item || viewer.world.getIndexOfItem(item) === -1) continue;
        if (index < viewer.world.getItemCount()) {
            viewer.world.setItemIndex(item, index);
        }
        index++;
    }
}

/**
 * Swap what is in `slot`, leaving whatever is already there on screen until the
 * new image has actually loaded. Returns the cleanup for the effect driving it.
 *
 * The incoming image is added transparent, so it can never flash in the wrong
 * place while it loads. That also means it has to be added with `preload`: OSD
 * skips fetching tiles for a fully transparent layer otherwise
 * (`getDrawArea` returns false, openseadragon.js:25301) and the swap would
 * simply never complete.
 */
function swapLayer(opts: {
    viewer: Viewer;
    slot: 'thumb' | 'base';
    tileSource: string | { type: string; url: string };
    layers: React.MutableRefObject<Layers>;
    gen: number;
    genRef: React.MutableRefObject<number>;
    // Runs as soon as the image is in the world, while it is still loading.
    // The clip goes on here rather than at install: a transparent item still
    // counts towards the home bounds, and an unclipped one would widen them.
    prepare: (item: OpenSeadragon.TiledImage) => void;
    // `wasEmpty` is true when this is the first layer to reach the world, which
    // is the one moment the viewport has to be fitted to the image.
    onInstalled: (item: OpenSeadragon.TiledImage, wasEmpty: boolean) => void;
}): () => void {
    const { viewer, slot, tileSource, layers, gen, genRef, prepare, onInstalled } =
        opts;
    let cancelled = false;
    let incoming: OpenSeadragon.TiledImage | null = null;
    let onLoaded:
        | ((e: OpenSeadragon.FullyLoadedChangeTiledImageEvent) => void)
        | null = null;

    const startedAt = performance.now();
    const label = typeof tileSource === 'string' ? shortId(tileSource) : slot;
    log('swap:start', { slot, gen, image: label });

    const superseded = () => cancelled || gen !== genRef.current;

    const drop = (item: OpenSeadragon.TiledImage) => {
        if (onLoaded) {
            item.removeHandler('fully-loaded-change', onLoaded);
            onLoaded = null;
        }
        try {
            viewer.world.removeItem(item);
        } catch {
            /* already gone with the viewer */
        }
    };

    viewer.addTiledImage({
        tileSource,
        index: 0,
        opacity: 0,
        preload: true,
        success: (event: Event) => {
            const item = (event as unknown as { item: OpenSeadragon.TiledImage })
                .item;
            incoming = item;
            if (superseded()) {
                drop(item);
                return;
            }
            prepare(item);
            const install = () => {
                if (superseded()) {
                    log('swap:superseded', {
                        slot,
                        gen,
                        image: label,
                        ms: Math.round(performance.now() - startedAt),
                    });
                    drop(item);
                    return;
                }
                if (onLoaded) {
                    item.removeHandler('fully-loaded-change', onLoaded);
                    onLoaded = null;
                }
                const previous = layers.current[slot];
                const wasEmpty = !layers.current.thumb && !layers.current.base;
                layers.current[slot] = item;
                if (previous) viewer.world.removeItem(previous);
                item.setPreload(false);
                restack(viewer, layers.current);
                // The number that matters: how long the user waited between
                // this layer being asked for and it being on screen.
                log('swap:installed', {
                    slot,
                    gen,
                    image: label,
                    ms: Math.round(performance.now() - startedAt),
                    // Tiled sources only; the thumbnail is a plain image.
                    tiles: (item.source as { maxLevel?: number })?.maxLevel,
                    worldItems: viewer.world.getItemCount(),
                });
                onInstalled(item, wasEmpty);
            };
            if (item.getFullyLoaded()) install();
            else {
                onLoaded = (
                    e: OpenSeadragon.FullyLoadedChangeTiledImageEvent,
                ) => {
                    if (e.fullyLoaded) install();
                };
                item.addHandler('fully-loaded-change', onLoaded);
            }
        },
    });

    return () => {
        cancelled = true;
        // Only take back an image that never got installed. Once it is the layer
        // on screen it belongs to the *next* swap, which removes it explicitly.
        // Removing it here would empty the world, and OSD then re-homes the
        // viewport when the next item arrives (openseadragon.js:9621).
        if (incoming && incoming !== layers.current[slot]) drop(incoming);
    };
}

interface ImageFileProps {
    image: ImageModel;
    displayMask: boolean;
    // When playback starts the viewer resets to the full view, silently.
    playing?: boolean;
    // Viewport rotation in degrees (e.g. -90). Uses OSD's native rotation so
    // every layer stays aligned.
    rotation?: number;
    // A pixel of the raw (unrotated) image to open on, from the `?x=&y=` deep
    // link. The viewer zooms to it once and keeps a marker on it while the
    // sample is browsed — the same physical spot on the tube across time.
    focus?: ViewerFocus | null;
    // An animal of the displayed image, bbox in raw (unrotated) jp2 pixels: the
    // viewer zooms to it and briefly flashes its box. A new `nonce` zooms
    // again, even on the same box (a second click after panning away).
    animalFocus?: { bbox: [number, number, number, number]; nonce: number } | null;
    // Extra controls floated over the bottom of the image (playback, from
    // ImageList). Rendered inside the viewer so they follow it to fullscreen.
    children?: React.ReactNode;
    // The scanner's black frame, in millimetres off each edge of the raw
    // (unrotated) image.
    crop?: Crop | null;
    // Where the scanner sits in the soil: draws the depth scale in a gutter
    // along the image's left edge, which widens the viewer by as much. None, no
    // scale and no gutter.
    depth?: DepthSetup | null;
    // Sizing from the parent. The viewer locks its own shape (the crop and the
    // rotation decide it), so it is given a height and takes its width.
    className?: string;
}

export default function ImageFile({
    image,
    displayMask,
    playing,
    rotation = 0,
    focus = null,
    animalFocus = null,
    children,
    crop = null,
    depth = null,
    className = '',
}: ImageFileProps) {
    const { projectId, sampleId } = useParams();
    const imageId = image.id;
    const {
        data: imageUrl,
        isLoading,
        isError,
    } = useViewerImageUrl(projectId, sampleId, imageId);
    const { data: thumbnailUrl, isError: thumbError } = useViewerThumbnailUrl(
        projectId,
        sampleId,
        imageId,
    );
    const { t } = useTranslation();

    const containerRef = useRef<HTMLDivElement | null>(null);
    const viewerRef = useRef<HTMLDivElement | null>(null);
    const viewerInstance = useRef<Viewer | null>(null);
    const layers = useRef<Layers>(noLayers());
    const baseGenRef = useRef(0);
    const thumbGenRef = useRef(0);

    const [ctrlPressed, setCtrlPressed] = useState(false);
    const effectiveDisplayMask = ctrlPressed ? !displayMask : displayMask;

    const [zoomed, setZoomed] = useState(false);
    const zoomedRef = useRef(false);
    const rotationRef = useRef(rotation);
    rotationRef.current = rotation;

    // Counts selection changes. Both layers lag behind the selection by
    // different amounts, and image ids carry no order, so this is what lets us
    // say which of two stale layers is the less stale one.
    const [selection, setSelection] = useState({ id: imageId, tick: 0 });
    if (selection.id !== imageId) {
        setSelection((s) => ({ id: imageId, tick: s.tick + 1 }));
    }
    const tick = selection.id === imageId ? selection.tick : selection.tick + 1;

    // What we have decided to show. `committed*` is the intent; `*Layer` is what
    // is actually installed in the world — they differ while a swap loads.
    const [committedImageUrl, setCommittedImageUrl] = useState<string | null>(
        null,
    );
    const [committedImageId, setCommittedImageId] = useState<string | null>(null);
    const [baseLayer, setBaseLayer] = useState<LayerState>(null);
    const [thumbLayer, setThumbLayer] = useState<LayerState>(null);
    const [masksGen, setMasksGen] = useState(0);

    // The masks follow the image the base layer is for, never the selected one.
    const { data: maskUrls } = useMaskUrls(projectId, committedImageId ?? undefined);

    // Show the full-res image whenever it is no staler than the thumbnail: it is
    // the sharper of the two, so it only gives way to a thumbnail that is
    // genuinely closer to the selection. During playback the full-res image is
    // pinned to where playback started while the thumbnail tracks every frame,
    // so this keeps it hidden for the whole run instead of letting it flash back
    // between frames.
    const showBase =
        !!baseLayer &&
        (baseLayer.id === imageId ||
            !thumbLayer ||
            baseLayer.tick >= thumbLayer.tick);
    const maskOpacity = showBase && effectiveDisplayMask ? MASK_OPACITY : 0;
    const maskOpacityRef = useRef(maskOpacity);
    maskOpacityRef.current = maskOpacity;
    const imageIdRef = useRef(imageId);
    imageIdRef.current = imageId;
    const thumbLayerRef = useRef(thumbLayer);
    thumbLayerRef.current = thumbLayer;
    // Read when a swap *starts*, never as a dependency: the tick advances on
    // every step, and re-running a swap effect on it would tear down an
    // in-flight tile load and start it again from scratch on each one.
    const tickRef = useRef(tick);
    tickRef.current = tick;

    // The deep-link target, read from inside the viewer callbacks rather than
    // captured, so a changed focus never restarts an in-flight swap.
    const focusRef = useRef(focus);
    focusRef.current = focus;
    const focusApplied = useRef(false);
    // Set when leaving fullscreen: the resize that follows refits the view
    // even if the user had zoomed in.
    const refitOnResize = useRef(false);

    // The scan's physical size in millimetres, which is what the crop is
    // measured against. Physical rather than pixels: one sample can hold
    // acquisitions at several DPIs, and playback shows thumbnails of one next to
    // the full-res layer of another. Estimated until a full-res layer has been
    // measured, with the DPI of the image it was committed for.
    const [measuredMm, setMeasuredMm] = useState<Size | null>(null);
    const physical: Size = measuredMm ?? {
        x: pxToMm(IMAGE_WIDTH, IMAGE_DPI),
        y: pxToMm(IMAGE_HEIGHT, IMAGE_DPI),
    };
    const physicalRef = useRef(physical);
    physicalRef.current = physical;
    const committedDpiRef = useRef<number | null | undefined>(null);
    const cropRef = useRef(crop);
    cropRef.current = crop;

    // Where the (cropped) image's top and bottom are on screen, in px down the
    // viewer, and the viewer's height: what the depth scale is laid out on.
    const [imageSpan, setImageSpan] = useState<{
        top: number;
        bottom: number;
        height: number;
    } | null>(null);

    const applyClip = useCallback((item: OpenSeadragon.TiledImage) => {
        item.setClip(clipFor(item, cropRef.current, physicalRef.current));
    }, []);

    /**
     * Where "the whole thing" is for this viewer: the full view normally, and
     * the deep-link pixel when there is one. Everything that used to call
     * goHome goes through here, so a rotation change or a sample switch can't
     * silently throw away the point the user was linked to.
     *
     * Only the full-res layer knows the real jp2 dimensions, so until it is
     * installed even a focused viewer falls back to the full view.
     */
    const resetView = useCallback((immediately = true) => {
        const viewer = viewerInstance.current;
        if (!viewer || viewer.world.getItemCount() === 0) return;
        const target = focusRef.current;
        const base = layers.current.base;
        if (target && base) {
            const size = base.getContentSize();
            const point = base.imageToViewportCoordinates(target.x, target.y);
            viewer.viewport.zoomTo(size.x / FOCUS_SPAN_PX, point, immediately);
            viewer.viewport.panTo(point, immediately);
            return;
        }
        viewer.viewport.goHome(immediately);
    }, []);

    // ---- Create the single, persistent viewer once ---------------------------
    useEffect(() => {
        if (!viewerRef.current) return;
        const viewer = new OpenSeadragon.Viewer({
            element: viewerRef.current,
            // OSD's own PNG buttons are replaced by the toolbar rendered below.
            showNavigationControl: false,
            // The masks come from S3 without CORS headers, which taints the
            // WebGL drawer and makes it fall back per-image anyway. Ask for
            // canvas outright so every layer renders the same way. (Switching
            // to webgl means serving the masks with CORS and setting
            // crossOriginPolicy here.)
            drawer: 'canvas',
            // We drive the viewport ourselves (goHome on first image, sample
            // change, play and rotation). Without this OSD also re-homes on its
            // own whenever an added item is the only one in the world
            // (openseadragon.js:9621), which loses the zoom behind our back.
            preserveViewport: true,
            imageLoaderLimit: TILE_LOADER_LIMIT,
            immediateRender: true,
            blendTime: 0,
            degrees: rotationRef.current,
            // OSD gives up on a tile after 30 s by default. An uncached tile
            // out of Cantaloupe has been measured at 14-33 s, so the default
            // was abandoning tiles that were merely slow and leaving a
            // permanent grey square in the middle of the scan. Once the
            // derivative cache is doing its job this ceiling is never reached.
            timeout: 60_000,
        });
        viewerInstance.current = viewer;

        // ---- Tile-level diagnostics ------------------------------------------
        // Failures are logged even with DEBUG off: a tile that never arrives is
        // a real fault, not noise, and it is invisible from the server side
        // when the cause is the browser giving up.
        const tileStarts = new Map<string, number>();
        const onTileLoadFailed = (e: OpenSeadragon.TileLoadFailedEvent) => {
            tileStarts.delete(e.tile?.url ?? '');
            console.warn('[osd] tile failed', {
                url: e.tile?.url,
                status: (e as unknown as { message?: string }).message,
                level: e.tile?.level,
            });
        };
        viewer.addHandler('tile-load-failed', onTileLoadFailed);

        // No OSD keyboard navigation once the canvas has focus: the arrows
        // belong to previous/next acquisition (ImageList), and OSD's own keys
        // would also rotate (r) or flip (f) the viewport behind the rotation we
        // drive. Zooming stays on the mouse and the toolbar.
        viewer.addHandler('canvas-key', (e) => {
            e.preventDefaultAction = true;
        });

        let onTileLoaded: ((e: OpenSeadragon.TileLoadedEvent) => void) | null =
            null;
        let onTileDrawing: ((e: OpenSeadragon.TileDrawingEvent) => void) | null =
            null;
        if (DEBUG) {
            // Per-tile server latency, as seen by the browser. Compare with the
            // duration in the Cantaloupe access log for the same region: a big
            // gap means queueing (in OSD or the browser), not decode time.
            onTileDrawing = (e: OpenSeadragon.TileDrawingEvent) => {
                const url = e.tile?.url;
                if (url && !tileStarts.has(url))
                    tileStarts.set(url, performance.now());
            };
            onTileLoaded = (e: OpenSeadragon.TileLoadedEvent) => {
                const url = e.tile?.url ?? '';
                const started = tileStarts.get(url);
                tileStarts.delete(url);
                log('tile', {
                    // The IIIF region/size, which is the part worth reading.
                    tile: url.split('/').slice(-4).join('/'),
                    level: e.tile?.level,
                    ms: started
                        ? Math.round(performance.now() - started)
                        : undefined,
                });
            };
            viewer.addHandler('tile-drawing', onTileDrawing);
            viewer.addHandler('tile-loaded', onTileLoaded);
        }

        const onViewportChange = () => {
            const home = viewer.viewport.getHomeZoom();
            const z = viewer.viewport.getZoom();
            if (!home || Number.isNaN(z)) return;
            const isZ = z > home * 1.02;
            if (isZ !== zoomedRef.current) {
                zoomedRef.current = isZ;
                setZoomed(isZ);
            }
        };
        viewer.addHandler('zoom', onViewportChange);
        viewer.addHandler('pan', onViewportChange);

        // Follow the image on screen for the depth scale, every frame the view
        // moves. The home bounds are the clipped image (see the header), and
        // pixelFromPoint applies the viewport rotation, so the image's top and
        // bottom are the extremes of its four corners.
        const onUpdateViewport = () => {
            if (viewer.world.getItemCount() === 0) return;
            const b = viewer.world.getHomeBounds();
            const ys = [
                b.getTopLeft(),
                b.getTopRight(),
                b.getBottomLeft(),
                b.getBottomRight(),
            ].map((p) => viewer.viewport.pixelFromPoint(p, true).y);
            const next = {
                top: Math.min(...ys),
                bottom: Math.max(...ys),
                height: viewer.container.clientHeight,
            };
            setImageSpan((prev) =>
                prev &&
                Math.abs(prev.top - next.top) < 0.5 &&
                Math.abs(prev.bottom - next.bottom) < 0.5 &&
                prev.height === next.height
                    ? prev
                    : next,
            );
        };
        viewer.addHandler('update-viewport', onUpdateViewport);

        // When the container changes size, OSD keeps the centre and scales the
        // zoom by the change in diagonal (openseadragon.js:11632) — which does
        // not keep a fitted image fitted. So a viewer at the full view is
        // refitted, as is one coming out of fullscreen, where whatever zoom
        // suited the big screen means nothing in the page. The decision is
        // taken now, while `zoomedRef` still describes the view before the
        // resize, and applied once OSD has finished its own adjustment, which
        // runs synchronously right after this event.
        const onResize = () => {
            const refit = refitOnResize.current || !zoomedRef.current;
            refitOnResize.current = false;
            if (refit) queueMicrotask(() => resetView(true));
        };
        viewer.addHandler('after-resize', onResize);
        return () => {
            viewer.removeHandler('after-resize', onResize);
            viewer.removeHandler('tile-load-failed', onTileLoadFailed);
            if (onTileDrawing) viewer.removeHandler('tile-drawing', onTileDrawing);
            if (onTileLoaded) viewer.removeHandler('tile-loaded', onTileLoaded);
            viewer.destroy();
            viewerInstance.current = null;
            layers.current = noLayers();
        };
    }, []);

    // ---- Commit the full-res image (debounced) -------------------------------
    // The timer starts when the image becomes selected, so it really is "stay
    // this long and you get the tiles".
    useEffect(() => {
        // Still presigning: keep whatever is on screen rather than tearing it
        // down for a flicker. The thumbnail advances on its own.
        if (isLoading) return;
        if (!imageUrl) {
            // No jp2 in S3 yet (the backend returns null), or the query failed.
            setCommittedImageId(null);
            setCommittedImageUrl(null);
            return;
        }
        const commit = () => {
            committedDpiRef.current = image.dpi;
            setCommittedImageId(imageId);
            setCommittedImageUrl(imageUrl);
        };
        // Nothing on screen yet, or nothing usable to wait behind: go straight
        // to the tiles. Zoomed in, the thumbnail can't stand in for them either.
        if (!layers.current.base || zoomedRef.current || thumbError) {
            commit();
            return;
        }
        const id = setTimeout(commit, VIEWER_LOAD_DEBOUNCE_MS);
        return () => clearTimeout(id);
        // `image` follows `imageId`: its DPI is read at commit, not tracked.
    }, [imageId, imageUrl, isLoading, thumbError]);

    // Zooming in on the thumbnail needs the real tiles now, not in a second.
    useEffect(() => {
        if (!zoomed || !imageUrl) return;
        committedDpiRef.current = image.dpi;
        setCommittedImageId(imageId);
        setCommittedImageUrl(imageUrl);
    }, [zoomed, imageId, imageUrl]);

    // ---- Swap the thumbnail layer -------------------------------------------
    // Keyed on the image, not the URL: presigned thumbnail URLs are re-issued
    // for the same image on every refetch, and re-adding the layer for that
    // would be pure churn. A thumbnail that fails to load simply never installs,
    // so the previous one stays up rather than blanking.
    // Zoomed in it is left alone entirely: a thumbnail magnified 20x is a smear,
    // and advancing it would push the full-res image out of the way (see
    // `showBase`) in favour of that smear. Zooming back out resumes it.
    useEffect(() => {
        const viewer = viewerInstance.current;
        // The ref, not the state: this effect is what sets it, so depending on
        // the state would restart the swap it just finished.
        if (
            !viewer ||
            !thumbnailUrl ||
            zoomed ||
            thumbLayerRef.current?.id === imageId
        )
            return;
        const gen = ++thumbGenRef.current;
        const requestedAt = tickRef.current;
        return swapLayer({
            viewer,
            slot: 'thumb',
            tileSource: { type: 'image', url: thumbnailUrl },
            layers,
            gen,
            genRef: thumbGenRef,
            prepare: applyClip,
            onInstalled: (item, wasEmpty) => {
                item.setOpacity(1);
                if (wasEmpty) viewer.viewport.goHome(true);
                setThumbLayer({ id: imageId, tick: requestedAt });
            },
        });
    }, [imageId, thumbnailUrl, zoomed]);

    // ---- Swap the full-res layer --------------------------------------------
    useEffect(() => {
        const viewer = viewerInstance.current;
        if (!viewer) return;

        if (!committedImageUrl || !committedImageId) {
            if (layers.current.base) {
                viewer.world.removeItem(layers.current.base);
                layers.current.base = null;
            }
            setBaseLayer(null);
            // Re-run the mask effect so its cleanup takes the now-orphaned
            // overlays down with the base they belonged to.
            setMasksGen((g) => g + 1);
            if (zoomedRef.current) {
                zoomedRef.current = false;
                setZoomed(false);
            }
            return;
        }

        const gen = ++baseGenRef.current;
        const id = committedImageId;
        const requestedAt = tickRef.current;
        return swapLayer({
            viewer,
            slot: 'base',
            tileSource: committedImageUrl,
            layers,
            gen,
            genRef: baseGenRef,
            prepare: (item) => {
                const px = item.getContentSize();
                const dpi = imageDpi(committedDpiRef.current, px.x);
                const mm = { x: pxToMm(px.x, dpi), y: pxToMm(px.y, dpi) };
                const known = physicalRef.current;
                // A tenth of a millimetre: below that, a DPI rounding is not a
                // different scan.
                if (
                    Math.abs(mm.x - known.x) > 0.1 ||
                    Math.abs(mm.y - known.y) > 0.1
                ) {
                    physicalRef.current = mm;
                    setMeasuredMm(mm);
                }
                applyClip(item);
            },
            onInstalled: (item, wasEmpty) => {
                // Same rule as `showBase`, applied here rather than waiting for
                // the effect below so the layer is never briefly invisible.
                const thumb = thumbLayerRef.current;
                const visible =
                    id === imageIdRef.current ||
                    !thumb ||
                    requestedAt >= thumb.tick;
                item.setOpacity(visible ? 1 : 0);
                if (wasEmpty) viewer.viewport.goHome(true);
                setBaseLayer({ id, tick: requestedAt });
                setMasksGen((g) => g + 1);
            },
        });
    }, [committedImageUrl, committedImageId]);

    // ---- Keep layer opacities in sync ---------------------------------------
    useEffect(() => {
        layers.current.base?.setOpacity(showBase ? 1 : 0);
        layers.current.gallery?.setOpacity(maskOpacity);
        layers.current.roots?.setOpacity(maskOpacity);
    }, [showBase, maskOpacity, masksGen]);

    // ---- Add masks for the installed base ------------------------------------
    useEffect(() => {
        const viewer = viewerInstance.current;
        const base = layers.current.base;
        if (!viewer || !base) return;
        const bounds = base.getBounds();
        let cancelled = false;
        const added: OpenSeadragon.TiledImage[] = [];

        const addMask = (slot: 'gallery' | 'roots', url?: string | null) => {
            if (!url) return;
            viewer.addTiledImage({
                tileSource: { type: 'image', url },
                x: bounds.x,
                y: bounds.y,
                width: bounds.width,
                opacity: 0,
                preload: true,
                success: (event: Event) => {
                    const item = (
                        event as unknown as { item: OpenSeadragon.TiledImage }
                    ).item;
                    if (cancelled) {
                        viewer.world.removeItem(item);
                        return;
                    }
                    layers.current[slot] = item;
                    added.push(item);
                    applyClip(item);
                    item.setPreload(false);
                    item.setOpacity(maskOpacityRef.current);
                    restack(viewer, layers.current);
                },
            });
        };
        addMask('gallery', maskUrls?.gallery);
        addMask('roots', maskUrls?.roots);

        return () => {
            cancelled = true;
            for (const item of added) {
                if (layers.current.gallery === item) layers.current.gallery = null;
                if (layers.current.roots === item) layers.current.roots = null;
                try {
                    viewer.world.removeItem(item);
                } catch {
                    /* already gone with the viewer */
                }
            }
        };
    }, [masksGen, maskUrls?.gallery, maskUrls?.roots]);

    // ---- Ctrl key ------------------------------------------------------------
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Control') setCtrlPressed(true);
        };
        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.key === 'Control') setCtrlPressed(false);
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    // ---- Switching samples starts fresh at the full view ---------------------
    useEffect(() => {
        resetView(true);
        if (zoomedRef.current) {
            zoomedRef.current = false;
            setZoomed(false);
        }
    }, [sampleId, resetView]);

    // ---- Pressing play resets the zoom to the full view (silently) -----------
    // Deep-linked, it stays on the point instead: watching one spot change over
    // time is the whole reason the link exists.
    useEffect(() => {
        if (!playing) return;
        resetView(true);
    }, [playing, resetView]);

    // ---- Open on the deep-link pixel, once ----------------------------------
    // The full-res layer is what defines the image coordinate space, so this
    // waits for it rather than guessing the dimensions from the thumbnail.
    useEffect(() => {
        if (!focus || focusApplied.current || !baseLayer) return;
        focusApplied.current = true;
        resetView(true);
    }, [focus, baseLayer, resetView]);

    // ---- Marker on the deep-link pixel --------------------------------------
    // Re-added whenever the full-res layer changes: the marker is anchored in
    // image coordinates, and a new acquisition is a new tiled image (possibly a
    // different DPI) to resolve them against.
    useEffect(() => {
        const viewer = viewerInstance.current;
        const base = layers.current.base;
        if (!viewer || !focus || !base) return;
        const marker = document.createElement('div');
        marker.style.cssText =
            'width:44px;height:44px;pointer-events:none;line-height:0;';
        marker.innerHTML = `<svg viewBox="0 0 44 44" width="44" height="44" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="22" cy="22" r="13" stroke="#1c2620" stroke-width="4" opacity="0.55"/>
            <circle cx="22" cy="22" r="13" stroke="#ff4d4f" stroke-width="1.75"/>
            <path d="M22 0v8M22 36v8M0 22h8M36 22h8" stroke="#1c2620" stroke-width="4" opacity="0.55"/>
            <path d="M22 0v8M22 36v8M0 22h8M36 22h8" stroke="#ff4d4f" stroke-width="1.75"/>
            <circle cx="22" cy="22" r="1.75" fill="#ff4d4f"/>
        </svg>`;
        viewer.addOverlay({
            element: marker,
            location: base.imageToViewportCoordinates(focus.x, focus.y),
            placement: OpenSeadragon.Placement.CENTER,
            checkResize: false,
        });
        return () => {
            try {
                viewer.removeOverlay(marker);
            } catch {
                /* already gone with the viewer */
            }
        };
    }, [focus, baseLayer]);

    // ---- Zoom to the selected animal, flashing its box ---------------------
    // Waits for the full-res layer of *this* image: it alone defines the image
    // coordinate space, and the previous acquisition's layer lingers while the
    // new one loads. The outline is anchored in image coordinates, so it turns
    // with the viewport like everything else. It shows for ANIMAL_FLASH_MS once
    // the zoom has landed, then fades: enough to spot the animal, without
    // hiding it afterwards.
    const animalZoomed = useRef<number | null>(null);
    useEffect(() => {
        const viewer = viewerInstance.current;
        const base = layers.current.base;
        if (!viewer || !animalFocus || !base || baseLayer?.id !== imageId) return;
        if (animalZoomed.current === animalFocus.nonce) return;
        animalZoomed.current = animalFocus.nonce;

        const [x, y, w, h] = animalFocus.bbox;
        const box = base.imageToViewportRectangle(x, y, w, h);
        const outline = document.createElement('div');
        outline.style.cssText = `pointer-events:none;border:2px solid #52b788;border-radius:3px;box-shadow:0 0 0 1px rgba(28,38,32,0.6);transition:opacity ${ANIMAL_FADE_MS}ms;`;
        viewer.addOverlay({ element: outline, location: box });

        const minSpan = base.imageToViewportRectangle(
            0,
            0,
            ANIMAL_MIN_SPAN_PX,
            ANIMAL_MIN_SPAN_PX,
        ).width;
        const sx = Math.max(box.width * ANIMAL_CONTEXT, minSpan);
        const sy = Math.max(box.height * ANIMAL_CONTEXT, minSpan);
        const c = box.getCenter();
        // fitBounds takes the viewport's rotation into account.
        viewer.viewport.fitBoundsWithConstraints(
            new OpenSeadragon.Rect(c.x - sx / 2, c.y - sy / 2, sx, sy),
        );

        const timers: number[] = [];
        const remove = () => {
            try {
                viewer.removeOverlay(outline);
            } catch {
                /* already gone with the viewer */
            }
        };
        let started = false;
        const startFlash = () => {
            if (started) return;
            started = true;
            timers.push(
                window.setTimeout(() => {
                    outline.style.opacity = '0';
                }, ANIMAL_FLASH_MS - ANIMAL_FADE_MS),
                window.setTimeout(remove, ANIMAL_FLASH_MS),
            );
        };
        // Count the second from when the zoom lands. Already on the animal,
        // there is no animation to finish: the fallback starts it anyway.
        viewer.addOnceHandler('animation-finish', startFlash);
        timers.push(window.setTimeout(startFlash, ANIMAL_FLASH_FALLBACK_MS));
        return () => {
            timers.forEach((id) => window.clearTimeout(id));
            viewer.removeHandler('animation-finish', startFlash);
            remove();
        };
    }, [animalFocus, baseLayer, imageId]);

    // ---- Re-clip everything when the crop, the size or the DPI changes -------
    // Every item in the world, not just the installed layers: a swap still
    // loading was clipped against the old values. The view is refitted unless
    // the user is zoomed in somewhere they chose — and, for a new measured
    // size, only if there is a crop for it to have moved.
    const reclip = useCallback(
        (refit: boolean) => {
            const viewer = viewerInstance.current;
            if (!viewer) return;
            for (let i = 0; i < viewer.world.getItemCount(); i++) {
                applyClip(viewer.world.getItemAt(i));
            }
            if (refit && !zoomedRef.current) resetView(true);
        },
        [applyClip, resetView],
    );
    useEffect(() => {
        reclip(true);
    }, [crop?.top, crop?.bottom, crop?.left, crop?.right, reclip]);
    useEffect(() => {
        reclip(hasCrop(cropRef.current));
    }, [measuredMm?.x, measuredMm?.y, reclip]);

    // ---- Apply rotation changes at runtime -----------------------------------
    useEffect(() => {
        const viewer = viewerInstance.current;
        if (!viewer) return;
        // `true` snaps straight to the new angle: switching between a clockwise
        // and a counter-clockwise scanner must not spin the image 180°.
        viewer.viewport.setRotation(rotation, true);
        resetView(true);
    }, [rotation, resetView]);

    // ---- Toolbar ---------------------------------------------------------------
    const zoomBy = (factor: number) => {
        const viewer = viewerInstance.current;
        if (!viewer) return;
        viewer.viewport.zoomBy(factor);
        viewer.viewport.applyConstraints();
    };

    // Fullscreen is taken on our container rather than through OSD's
    // setFullScreen, which only takes the canvas and would leave the overlaid
    // controls behind. OSD's autoResize follows the new size on its own.
    const [fullscreen, setFullscreen] = useState(false);
    useEffect(() => {
        const onChange = () => {
            const isFullscreen =
                document.fullscreenElement === containerRef.current;
            // Leaving fullscreen lands back on the full view.
            if (!isFullscreen) refitOnResize.current = true;
            setFullscreen(isFullscreen);
        };
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);
    const toggleFullscreen = () => {
        if (document.fullscreenElement) void document.exitFullscreen();
        else void containerRef.current?.requestFullscreen();
    };

    // Nothing has ever loaded: the canvas is empty, so say why.
    const nothingOnScreen = !thumbLayer && !baseLayer;

    // The misleading case: what is on screen is another acquisition than the
    // one selected, because the selected one is still loading. Not "tiles still
    // arriving for the right image" — that shows for itself — and not during
    // playback, where the thumbnail trailing a frame is the normal state.
    // Nor once the selected image has nothing left that could arrive (no jp2
    // and a thumbnail that failed), or it would spin forever.
    const currentOnScreen =
        thumbLayer?.id === imageId || (showBase && baseLayer?.id === imageId);
    const nothingComing = thumbError && !isLoading && !imageUrl;
    const stale =
        !nothingOnScreen && !currentOnScreen && !playing && !nothingComing;
    const [showStale, setShowStale] = useState(false);
    useEffect(() => {
        if (!stale) {
            setShowStale(false);
            return;
        }
        const id = setTimeout(() => setShowStale(true), STALE_INDICATOR_DELAY_MS);
        return () => clearTimeout(id);
    }, [stale]);

    // A quarter-turn swaps the rendered bounding box to landscape, so the locked
    // container aspect swaps with it. A crop changes the shape too.
    const shown = hasCrop(crop)
        ? croppedSize(physical.x, physical.y, crop)
        : { width: IMAGE_WIDTH, height: IMAGE_HEIGHT };
    const rotated = ((rotation % 180) + 180) % 180 === 90;
    const aspectW = rotated ? shown.height : shown.width;
    const aspectH = rotated ? shown.width : shown.height;
    // The image's real height on screen, from the top of the glass.
    const keptMm = croppedSize(physical.x, physical.y, crop ?? NO_CROP);
    const imageHeightCm = (rotated ? keptMm.width : keptMm.height) / 10;
    // Beside the image, not over it: reserved whenever there is a depth setup,
    // so the image doesn't move when the scale comes.
    const gutter = depth ? DEPTH_GUTTER_PX : 0;

    return (
        // The aspect ratio is the image's: content-box, so the gutter comes on
        // top of it as padding instead of out of it. In fullscreen the browser
        // takes over the sizing and the image is fitted in what is left.
        <div
            ref={containerRef}
            className={`group relative box-content [&:fullscreen]:bg-[#f4f5f1] ${className}`}
            style={{
                aspectRatio: `${aspectW} / ${aspectH}`,
                paddingLeft: gutter,
                maxWidth: `calc(100% - ${gutter}px)`,
            }}
        >
            {/* Top-aligned with the frame, so the image's position in the
                viewer is its position here. */}
            {depth && imageSpan && !nothingOnScreen && (
                <div
                    className="absolute inset-y-0 left-0"
                    style={{ width: gutter }}
                >
                    <DepthScale
                        setup={depth}
                        imageTopPx={imageSpan.top}
                        imageBottomPx={imageSpan.bottom}
                        imageHeightCm={imageHeightCm}
                        viewHeightPx={imageSpan.height}
                    />
                </div>
            )}
            <div
                className="absolute inset-y-0 right-0 bg-[#1c2620] rounded-md overflow-hidden ring-1 ring-[#dadbd5]"
                style={{ left: gutter }}
            >
                <div ref={viewerRef} className="absolute inset-0" />

                {/* Overlaid controls: muted until the pointer is over the image so
                    they never compete with it, but always there to be found. */}
                {!nothingOnScreen && (
                    <div className="pointer-events-none absolute inset-0 opacity-70 transition-opacity duration-200 group-hover:opacity-100">
                        <ViewerToolbar
                            orientation="vertical"
                            className="absolute right-3 top-3"
                        >
                            <ViewerButton
                                onClick={() => zoomBy(ZOOM_STEP)}
                                aria-label={t('Zoom in')}
                                title={t('Zoom in')}
                            >
                                <ZoomInIcon />
                            </ViewerButton>
                            <ViewerButton
                                onClick={() => zoomBy(1 / ZOOM_STEP)}
                                disabled={!zoomed}
                                aria-label={t('Zoom out')}
                                title={t('Zoom out')}
                            >
                                <ZoomOutIcon />
                            </ViewerButton>
                            {/* A house, as deep-zoom and map viewers have it, for
                                the whole image back in view; the corners are
                                fullscreen's alone. Like zoom out, only live once
                                zoomed. */}
                            <ViewerButton
                                onClick={() => resetView(false)}
                                disabled={!zoomed}
                                aria-label={t('Back to the whole image')}
                                title={t('Back to the whole image')}
                            >
                                <HomeIcon />
                            </ViewerButton>
                            <div className="my-0.5 h-px w-5 bg-white/15" />
                            <ViewerButton
                                onClick={toggleFullscreen}
                                aria-label={
                                    fullscreen ? t('Exit fullscreen') : t('Fullscreen')
                                }
                                title={
                                    fullscreen ? t('Exit fullscreen') : t('Fullscreen')
                                }
                            >
                                {fullscreen ? <MinimizeIcon /> : <MaximizeIcon />}
                            </ViewerButton>
                        </ViewerToolbar>
                        {(children || showStale) && (
                            <div className="absolute inset-x-0 bottom-3 flex justify-center">
                                {/* The indicator hangs off the side of the controls
                                    so they never shift when it comes and goes. */}
                                <div className="relative">
                                    {children}
                                    {showStale && (
                                        <ViewerToolbar
                                            className="absolute left-full top-1/2 -translate-y-1/2 ml-2 p-2"
                                            role="status"
                                            aria-label={t('Loading image')}
                                            title={t('Loading image')}
                                        >
                                            <Loader2Icon className="h-4 w-4 animate-spin text-white" />
                                        </ViewerToolbar>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {nothingOnScreen && (
                    <>
                        {isLoading ? (
                            <Skeleton className="absolute inset-0 w-full h-full rounded-md" />
                        ) : isError ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-[#a8aea3]">
                                <ImageOffIcon className="h-8 w-8 mx-auto mb-2" />
                                <p className="text-sm">{t('Error loading image')}</p>
                            </div>
                        ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <p className="text-[#a8aea3] text-sm">
                                    {t('No image available')}
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
